<script setup>
import { computed, defineProps } from "vue"
import { useI18n } from "vue-i18n"

const { t } = useI18n()

const props = defineProps({
  password: {
    type: String,
    required: true,
  },
})

const requirements = computed(() => {
  const password = props.password || ""
  return {
    length: password.length >= 8,
    uppercase: /[A-Z]/.test(password),
    lowercase: /[a-z]/.test(password),
    number: /[0-9]/.test(password),
    // Escaped the dollar sign for consistency across environments.
    special: /[@\$!%*?&]/.test(password),
  }
})
const strengthScore = computed(() => {
  if (props.password === "") return 0
  return Object.values(requirements.value).filter(Boolean).length
})

const strengthLevel = computed(() => {
  if (props.password === "") return "blank"
  if (strengthScore.value <= 1) return "very-weak"
  if (!requirements.value.length) return "weak"
  if (strengthScore.value <= 2) return "weak"
  if (
    strengthScore.value >= 3 &&
    props.password.length <= 8 &&
    requirements.value.special &&
    requirements.value.number &&
    requirements.value.uppercase &&
    requirements.value.lowercase
  )
    return "good"
  if (
    strengthScore.value >= 4 &&
    props.password.length >= 8 &&
    requirements.value.special &&
    requirements.value.number &&
    requirements.value.uppercase &&
    requirements.value.lowercase
  )
    return "strong"
  return "weak"
})

const strengthLevelScore = computed(() => {
  switch (strengthLevel.value) {
    case "blank":
      return 0
    case "very-weak":
      return 1
    case "weak":
      return 2
    case "good":
      return 3
    case "strong":
      return 4
    default:
      return 0
  }
})

const strengthColors = {
  blank: "bg-gray-400",
  "very-weak": "bg-[#D42C2A]",
  weak: "bg-[#f3b243]",
  good: "bg-green-600",
  strong: "bg-[#028657]",
}

const strengthText = computed(() => {
  const levels = {
    blank: t("common.password-strength.strength"),
    "very-weak": t("common.password-strength.very-weak"),
    weak: t("common.password-strength.weak"),
    good: t("common.password-strength.good"),
    strong: t("common.password-strength.strong"),
  }
  return levels[strengthLevel.value]
})
</script>
<template>
  <div class="w-full">
    <div class="mt-2">
      <div class="flex gap-1 mb-1">
        <div
          v-for="(segment, index) in 4"
          :key="index"
          class="h-2 flex-1 rounded-full transition-all duration-300"
          :class="[
            index < strengthLevelScore
              ? strengthColors[strengthLevel]
              : 'bg-gray-200',
          ]"
        ></div>
      </div>
      <div class="flex justify-end items-center text-sm">
        <div>
          <span
            class="font-medium transition-colors duration-300 text-[#7F7F7F]"
          >
            {{ strengthText }}
          </span>
        </div>
      </div>
    </div>
  </div>
</template>
